/**
 * The Orbit Menu — navigation that lives inside the Orb (NAVIGATION.md §3).
 *
 * There is no bottom bar any more. On an inner screen the small orb in the
 * header is the way around: tapping it dims the screen, puts a breathing Orb in
 * the middle of it, and flies the five circles out of that Orb into a ring.
 *
 * One instance, mounted on <body> as the last child, so there is a single fixed
 * layer and no z-index argument with anything (§5). Screens do not own it; they
 * only ask it to open.
 *
 * Geometry is the document's: R = 128px, circles at −90°, −18°, 54°, 126°, 198°,
 * clockwise from the top. R is capped against the viewport so an iPhone SE keeps
 * its margin instead of pushing a circle off the edge.
 */

import { SECTIONS } from './chrome.js'
import { mountQuietOrb } from './orb.js'
import { icon } from '../js/icons.js'
import { navigate } from '../js/router.js'
import { text } from '../js/html.js'

/** Clockwise from the top, one per section, in SECTIONS order. */
const ANGLES = [-90, -18, 54, 126, 198]

const OPEN_MS = 260
const CLOSE_MS = 180
const STAGGER_MS = 40

let overlay = null
let orb = null
let opened = false
let returnFocus = null
let pushedHistory = false
let closeTimer = null
let onClosed = null

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function tick() {
  if (navigator.vibrate) navigator.vibrate(10)
}

export function isOrbitOpen() {
  return opened
}

/**
 * Opens the menu.
 *
 * `from` is the header orb button: the central Orb grows out of exactly where
 * the thing you tapped is, which is the whole reason the gesture reads as the
 * menu coming out of the Orb rather than a panel appearing.
 *
 * @param {{from?: Element|null, current?: string|null, alerts?: string[],
 *   onClosed?: () => void}} options
 */
export function openOrbitMenu({ from = null, current = null, alerts = [], onClosed: whenClosed = null } = {}) {
  if (opened) return

  window.clearTimeout(closeTimer)
  returnFocus = from ?? document.activeElement
  onClosed = whenClosed

  overlay = document.createElement('div')
  overlay.className = 'orbit'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', 'Navigation')
  overlay.innerHTML = markup(current, alerts)

  if (reducedMotion()) {
    overlay.classList.add('orbit--still')
  }

  // Where the central orb grows from, relative to where it ends up.
  const origin = from?.getBoundingClientRect?.()

  if (origin && !reducedMotion()) {
    const stage = overlay.querySelector('.orbit__stage')
    const centre = stageCentre()

    stage.style.setProperty('--from-x', `${Math.round(origin.left + origin.width / 2 - centre.x)}px`)
    stage.style.setProperty('--from-y', `${Math.round(origin.top + origin.height / 2 - centre.y)}px`)
  }

  document.body.append(overlay)
  document.body.classList.add('is-orbiting')

  orb = mountQuietOrb(overlay.querySelector('.orbit__orb-face'))

  opened = true
  tick()

  // Back closes the menu, not the screen. The path does not change, so the
  // router knows to leave the screen alone when this entry is popped.
  window.history.pushState({ orbit: true }, '', window.location.pathname)
  pushedHistory = true

  overlay.addEventListener('click', onOverlayClick)
  overlay.addEventListener('keydown', onKeyDown)

  overlay.querySelector('[data-orbit-item]')?.focus()
}

/**
 * Closes the menu.
 *
 * `consumed` means the history entry this menu pushed is already being spent —
 * either the browser popped it, or we are about to replace it with the screen
 * being opened. Calling back() again in those cases would take the person a
 * screen further than they asked.
 */
export function closeOrbitMenu({ consumed = false } = {}) {
  if (!opened) return

  const dying = overlay

  opened = false
  overlay = null

  dying.removeEventListener('click', onOverlayClick)
  dying.removeEventListener('keydown', onKeyDown)
  dying.classList.add('orbit--closing')

  document.body.classList.remove('is-orbiting')

  const finish = () => {
    orb?.destroy()
    orb = null
    dying.remove()
  }

  if (reducedMotion()) {
    finish()
  } else {
    closeTimer = window.setTimeout(finish, CLOSE_MS)
  }

  returnFocus?.focus?.()
  returnFocus = null

  onClosed?.()
  onClosed = null

  if (!consumed && pushedHistory) {
    window.history.back()
  }

  pushedHistory = false
}

// ---- markup ----------------------------------------------------------------

function markup(current, alerts) {
  const radius = 'var(--orbit-r)'

  const items = SECTIONS.map((section, index) => {
    const angle = (ANGLES[index] * Math.PI) / 180
    const x = Math.cos(angle)
    const y = Math.sin(angle)
    const active = section.key === current
    const alert = alerts.includes(section.key) && !active

    return `
      <a class="orbit__item ${active ? 'orbit__item--here' : ''}"
         href="${section.path}"
         data-orbit-item
         data-orbit-key="${text(section.key)}"
         ${active ? 'aria-current="page"' : ''}
         style="--x:calc(${radius} * ${x.toFixed(4)});--y:calc(${radius} * ${y.toFixed(4)});--i:${index}">
        <span class="orbit__circle">
          ${alert ? '<span class="orbit__led" aria-hidden="true"></span>' : ''}
          ${icon(section.icon, { size: 24, strokeWidth: 1.6 })}
        </span>
        <span class="orbit__label">${text(section.label)}</span>
      </a>
    `
  }).join('')

  return `
    <div class="orbit__stage">
      <span class="orbit__ring" aria-hidden="true"></span>
      <button type="button" class="orbit__orb" data-orbit-close aria-label="Close navigation">
        <span class="orbit__orb-face"></span>
      </button>
      ${items}
    </div>
    <a class="orbit__practical" href="/prakticno" data-orbit-item data-orbit-key="practical">
      Practical info
    </a>
  `
}

/** Where the stage's centre sits on screen, for the morph origin. */
function stageCentre() {
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 - 16 }
}

// ---- behaviour -------------------------------------------------------------

function onOverlayClick(event) {
  const item = event.target.closest('[data-orbit-item]')

  if (item !== null) {
    event.preventDefault()
    select(item.getAttribute('href'))

    return
  }

  // The central orb, or anywhere outside the circles: close, stay put.
  closeOrbitMenu()
}

function select(path) {
  tick()

  const here = path === window.location.pathname

  // Replacing, not pushing: the entry being replaced is the one this menu
  // pushed, so back from the new screen lands on the screen you left.
  closeOrbitMenu({ consumed: !here })

  if (!here) {
    navigate(path, { replace: true })
  }
}

function onKeyDown(event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    closeOrbitMenu()

    return
  }

  const items = [...overlay.querySelectorAll('[data-orbit-item]')]
  const index = items.indexOf(document.activeElement)

  if (event.key === 'Tab') {
    event.preventDefault()
    const step = event.shiftKey ? -1 : 1
    items[(index + step + items.length) % items.length].focus()

    return
  }

  if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) {
    event.preventDefault()
    const step = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1
    items[(index + step + items.length) % items.length].focus()
  }
}

/**
 * The browser's back gesture, or ours. Either way the entry the menu pushed is
 * gone by the time this runs, so it must not ask for another one.
 */
window.addEventListener('popstate', () => {
  if (opened) {
    closeOrbitMenu({ consumed: true })
  }
})
